import { ForwardedRef, forwardRef, useContext } from 'react'
import cx from 'classnames'
import { useOption } from '@react-aria/listbox'
import { useObjectRef } from 'utils/useObjectRef'
import { VirtualizedOptionProps } from '../Types'
import Styles from '../Option.module.scss'
import { Check2 } from 'blaze-ui/icons'
import { ListBoxContext } from '../ListBoxContext'
import { ListState } from '@react-stately/list'

function Option<T extends object>({ item, className }: VirtualizedOptionProps<T>, ref: ForwardedRef<HTMLLIElement>) {
  const state = useContext(ListBoxContext)
  if (!state) {
    return null
  }

  return <OptionWithState item={item} className={className} state={state} ref={ref} />
}

function _OptionWithState<T extends object>(
  { item, className, state }: VirtualizedOptionProps<T> & { state: ListState<T> },
  fRef: ForwardedRef<HTMLLIElement>,
) {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const ref = useObjectRef(fRef)
  const { rendered, key } = item
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { optionProps, isSelected, isDisabled } = useOption(
    {
      key,
      shouldFocusOnHover: false,
      isVirtualized: true,
    },
    state,
    ref,
  )

  return (
    <li
      {...optionProps}
      ref={ref}
      className={cx(Styles.root, className, {
        [Styles.focused]: state.selectionManager.focusedKey === key,
        [Styles.disabled]: isDisabled,
        [Styles.selected]: isSelected,
      })}
    >
      <div className={Styles.content}>{rendered}</div>
      {isSelected && (
        <div className={Styles.checkIcon}>
          <Check2 size={16} />
        </div>
      )}
    </li>
  )
}

const OptionWithState = forwardRef(_OptionWithState)
const _Option = forwardRef(Option)
export { _Option as Option }
